import { useMedplum } from '@medplum/react';
import {
  Patient,
  Encounter,
  PractitionerRole,
  Practitioner,
} from '@medplum/fhirtypes';
import {
  buildEncounterFromForm,
  EncounterFormData,
} from '@/utils/encounter-builder';
import { formatHumanName } from '@medplum/core';
import { ARKON } from '@consts/systems.consts';

const PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM = 'patient-assignment';

export const useEncounterOperations = () => {
  const medplum = useMedplum();

  const createEncounter = async (
    patient: Patient,
    formData: EncounterFormData
  ): Promise<Encounter> => {
    const encounter = buildEncounterFromForm(patient, formData);
    const createdEncounter = await medplum.createResource(encounter);

    // Helper function to create PractitionerRole
    const createPractitionerRole = async (
      practitioner: Partial<Practitioner>,
      roleCode: string,
      roleDisplay: string
    ) => {
      if (!practitioner?.id) {
        return;
      }

      try {
        // Check if PractitionerRole already exists for this patient-practitioner combination
        const existingRoles = await medplum.searchResources(
          'PractitionerRole',
          {
            identifier: `${PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM}|${patient.id}`,
            practitioner: `Practitioner/${practitioner.id}`,
          }
        );

        // Check if there's already an active role with the same code
        const hasExistingRole = existingRoles.some(
          (role) =>
            role.code?.[0]?.coding?.[0]?.code === roleCode &&
            role.active !== false
        );

        // Only create if it doesn't already exist
        if (!hasExistingRole) {
          const practitionerRole: PractitionerRole = {
            resourceType: 'PractitionerRole',
            practitioner: {
              reference: `Practitioner/${practitioner.id}`,
              display: formatHumanName(practitioner.name?.[0]),
            },
            code: [
              {
                coding: [
                  {
                    system: ARKON.CS.PRACTITIONER.ROLE.BASE,
                    code: roleCode,
                    display: roleDisplay,
                  },
                ],
              },
            ],
            identifier: [
              {
                system: PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM,
                value: patient.id,
              },
            ],
            active: true,
          };

          await medplum.createResource(practitionerRole);
        }
      } catch (error) {
        // Log error but don't fail the encounter creation
        console.error(
          `Error creating PractitionerRole for doctor ${practitioner.id}:`,
          error
        );
      }
    };

    // Create PractitionerRole for attending doctor with role "assigned"
    if (formData.attendingDoctor) {
      await createPractitionerRole(
        formData.attendingDoctor,
        ARKON.CS.PRACTITIONER.ROLE.CODES.ASSIGNED,
        'Assigned Doctor'
      );
    }

    // Create PractitionerRole resources for referring doctors
    if (formData.referringDoctors && formData.referringDoctors.length > 0) {
      await Promise.all(
        formData.referringDoctors
          .filter((practitioner) => practitioner?.id)
          .map((practitioner) =>
            createPractitionerRole(
              practitioner,
              ARKON.CS.PRACTITIONER.ROLE.CODES.REFERRAL,
              'Referral Doctor'
            )
          )
      );
    }

    return createdEncounter;
  };

  const updateEncounter = async (
    patient: Patient,
    formData: EncounterFormData,
    existingEncounter: Encounter
  ): Promise<Encounter> => {
    const updatedEncounter = buildEncounterFromForm(patient, formData);
    updatedEncounter.id = existingEncounter.id;
    updatedEncounter.meta = existingEncounter.meta;

    const updated = await medplum.updateResource(updatedEncounter);
    return updated;
  };

  return {
    createEncounter,
    updateEncounter,
  };
};
